import React from 'react'

export interface IRequestContextValue {
  search: string
  setSearch: (search: string) => void
  orderBy: string
  setOrderBy: (orderBy: string) => void
  fields: string
  setFields: (fields: string) => void
  hasData: boolean
  setHasData: (value: boolean) => void
  total: number
  setTotal: (total: number) => void
}

export const RequestContext = React.createContext<IRequestContextValue>({
  search: '',
  setSearch: (_search: string) => {},
  orderBy: '',
  setOrderBy: (_orderBy: string) => {},
  fields: '',
  setFields: (_fields: string) => {},
  hasData: false,
  setHasData: (_value: boolean) => {},
  total: 0,
  setTotal: (_total: number) => {}
})

export const RequestContextProvider: React.FC = props => {
  const [search, setSearch] = React.useState('')
  const [orderBy, setOrderBy] = React.useState('')
  const [fields, setFields] = React.useState('')
  const [total, setTotal] = React.useState(0)
  const [hasData, setHasData] = React.useState(false)

  const mounted = React.useRef(false)

  React.useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false
    }
  }, [])

  const memoizedSetSearch = React.useCallback(
    (search: string) => {
      if (mounted.current) setSearch(search)
    },
    [setSearch, mounted.current]
  )

  const memoizedSetOrderBy = React.useCallback(
    (orderBy: string) => {
      if (mounted.current) setOrderBy(orderBy)
    },
    [setOrderBy, mounted.current]
  )

  const memoizedSetFields = React.useCallback(
    (fields: string) => {
      if (mounted.current) setFields(fields)
    },
    [setFields, mounted.current]
  )

  const memoizedSetHasData = React.useCallback(
    (value: boolean) => {
      if (mounted.current) setHasData(value)
    },
    [setSearch, mounted.current]
  )

  const memoizedSetTotal = React.useCallback(
    (total: number) => {
      if (mounted.current) setTotal(total)
    },
    [setTotal, mounted.current]
  )

  const value = React.useMemo(() => {
    return {
      search,
      setSearch: memoizedSetSearch,
      orderBy,
      setOrderBy: memoizedSetOrderBy,
      fields,
      setFields: memoizedSetFields,
      hasData,
      setHasData: memoizedSetHasData,
      total,
      setTotal: memoizedSetTotal
    }
  }, [search, orderBy, fields, total, hasData])

  return <RequestContext.Provider value={value}>{props.children}</RequestContext.Provider>
}

export const useRequestContext = () => React.useContext(RequestContext)
